import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    ReactNode,
    useCallback,
} from 'react';
import axiosApexInstance from "../auth/axiosApexInstance";
import { AppNavItem, NavItemFormData, ReorderData, NavItemWithChildren } from '../types/navigation';
import { useAppAuth } from './AppAuthContext';
import { useUser } from './UserContext';

interface NavigationContextType {
    navItems: AppNavItem[];
    navItemsWithChildren: NavItemWithChildren[];
    loading: boolean;
    error: string | null;
    fetchNavItems: () => Promise<void>;
    fetchEnabledNavItems: () => Promise<void>;
    addNavItem: (navItem: NavItemFormData) => Promise<void>;
    updateNavItem: (id: number, navItem: NavItemFormData) => Promise<void>;
    deleteNavItem: (id: number) => Promise<void>;
    toggleEnabled: (id: number, isEnabled: number) => Promise<void>;
    toggleDivider: (id: number, addDividerBefore: number) => Promise<void>;
    reorderItems: (reorderData: ReorderData[]) => Promise<void>;
    getGroups: () => AppNavItem[];
    getItemsByGroup: (groupId: number) => AppNavItem[];
    validateNavItem: (navItem: NavItemFormData) => boolean;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [navItems, setNavItems] = useState<AppNavItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { authKeys } = useAppAuth();
    const { accessToken } = useUser();

    const fetchNavItems = useCallback(async () => {
        // Don't fetch if access token is not available yet
        if (!accessToken) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await axiosApexInstance.get('/task/appnav/items');
            setNavItems(response.data);
        } catch (err: any) {
            // Only log if it's not a missing token error (expected during initial load)
            if (!err?.message?.includes('Missing access token')) {
                console.error('Error fetching navigation items:', err);
            }
            setError('Failed to fetch navigation items');
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    const fetchEnabledNavItems = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch ALL enabled items (no server-side filtering)
            const response = await axiosApexInstance.get('/task/appnav/items/enabled');
            setNavItems(response.data);
        } catch (err: any) {
            console.error('Error fetching enabled navigation items:', err);
            const errorMessage = err.response?.data?.message || err.response?.statusText || err.message || 'Failed to fetch enabled navigation items';
            setError(`Failed to fetch enabled navigation items: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    }, []);

    const addNavItem = useCallback(async (navItem: NavItemFormData) => {
        setLoading(true);
        setError(null);
        try {
            await axiosApexInstance.post('/task/appnav/items', navItem);
            await fetchNavItems(); // Refresh the list with all items
        } catch (err: any) {
            console.error('Error adding navigation item:', err);
            setError(err.response?.data || 'Failed to add navigation item');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchNavItems]);

    const updateNavItem = useCallback(async (id: number, navItem: NavItemFormData) => {
        setLoading(true);
        setError(null);
        try {
            await axiosApexInstance.put(`/task/appnav/items/${id}`, navItem);
            await fetchNavItems(); // Refresh the list with all items
        } catch (err: any) {
            console.error('Error updating navigation item:', err);
            setError(err.response?.data || 'Failed to update navigation item');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchNavItems]);

    const deleteNavItem = useCallback(async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            await axiosApexInstance.delete(`/task/appnav/items/${id}`);
            await fetchNavItems(); // Refresh the list with all items
        } catch (err) {
            console.error('Error deleting navigation item:', err);
            setError('Failed to delete navigation item');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchNavItems]);

    const toggleEnabled = useCallback(async (id: number, isEnabled: number) => {
        setLoading(true);
        setError(null);
        try {
            await axiosApexInstance.put(`/task/appnav/items/${id}/toggle-enabled?isEnabled=${isEnabled}`);
            await fetchNavItems(); // Refresh the list with all items (enabled and disabled)
        } catch (err) {
            console.error('Error toggling navigation item:', err);
            setError('Failed to toggle navigation item');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchNavItems]);

    const toggleDivider = useCallback(async (id: number, addDividerBefore: number) => {
        setLoading(true);
        setError(null);
        try {
            await axiosApexInstance.put(`/task/appnav/items/${id}/toggle-divider?addDividerBefore=${addDividerBefore}`);
            await fetchNavItems(); // Refresh the list with all items
        } catch (err) {
            console.error('Error toggling divider:', err);
            setError('Failed to toggle divider');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchNavItems]);

    const reorderItems = useCallback(async (reorderData: ReorderData[]) => {
        setLoading(true);
        setError(null);
        try {
            await axiosApexInstance.post('/task/appnav/items/reorder', reorderData);
            await fetchNavItems(); // Refresh the list with all items
        } catch (err) {
            console.error('Error reordering navigation items:', err);
            setError('Failed to reorder navigation items');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchNavItems]);

    const getGroups = useCallback(() => {
        return navItems.filter(item => item.itemType === 'group');
    }, [navItems]);

    const getItemsByGroup = useCallback((groupId: number) => {
        return navItems.filter(item => item.parentGroupId === groupId);
    }, [navItems]);

    const validateNavItem = useCallback((navItem: NavItemFormData): boolean => {
        if (!navItem.itemKey || navItem.itemKey.trim() === '') {
            console.log('Validation failed: itemKey is empty');
            return false;
        }
        if (!navItem.label || navItem.label.trim() === '') {
            console.log('Validation failed: label is empty');
            return false;
        }
        if (!navItem.itemType) {
            console.log('Validation failed: itemType is empty');
            return false;
        }

        // Validate item type
        if (!['group', 'item', 'divider'].includes(navItem.itemType)) {
            console.log('Validation failed: invalid itemType:', navItem.itemType);
            return false;
        }

        // For items, validate required fields
        if (navItem.itemType === 'item') {
            if (!navItem.path || navItem.path.trim() === '') {
                console.log('Validation failed: path is empty for item type');
                return false;
            }
        }

        // Check if auth key exists (if provided)
        if (navItem.authKey && navItem.authKey.trim() !== '') {
            console.log('Validating auth key:', navItem.authKey);
            console.log('Available auth keys:', authKeys.map(k => k.authKey));
            
            if (authKeys.length === 0) {
                console.log('Validation failed: no auth keys loaded yet');
                return false;
            }
            
            const authKeyExists = authKeys.some(key => key.authKey === navItem.authKey);
            console.log('Auth key exists:', authKeyExists);
            
            if (!authKeyExists) {
                console.log('Validation failed: auth key not found:', navItem.authKey);
                return false;
            }
        }

        console.log('Validation passed for nav item:', navItem);
        return true;
    }, [authKeys]);

    // Build hierarchical structure
    const navItemsWithChildren = useCallback((): NavItemWithChildren[] => {
        const itemsMap = new Map<number, NavItemWithChildren>();
        const result: NavItemWithChildren[] = [];

        // First pass: create map of all items
        navItems.forEach(item => {
            itemsMap.set(item.id!, { ...item, children: [] });
        });

        // Second pass: build hierarchy
        navItems.forEach(item => {
            const navItemWithChildren = itemsMap.get(item.id!)!;
            
            if (item.parentGroupId === null || item.parentGroupId === undefined) {
                result.push(navItemWithChildren);
            } else {
                const parent = itemsMap.get(item.parentGroupId);
                if (parent) {
                    parent.children = parent.children || [];
                    parent.children.push(navItemWithChildren);
                }
            }
        });

        return result.sort((a, b) => a.displayIndex - b.displayIndex);
    }, [navItems]);

    useEffect(() => {
        // Only fetch if access token is available
        if (accessToken) {
            fetchNavItems();
        }
    }, [fetchNavItems, accessToken]);

    return (
        <NavigationContext.Provider
            value={{
                navItems,
                navItemsWithChildren: navItemsWithChildren(),
                loading,
                error,
                fetchNavItems,
                fetchEnabledNavItems,
                addNavItem,
                updateNavItem,
                deleteNavItem,
                toggleEnabled,
                toggleDivider,
                reorderItems,
                getGroups,
                getItemsByGroup,
                validateNavItem,
            }}
        >
            {children}
        </NavigationContext.Provider>
    );
};

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (context === undefined) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
}; 